type TranslateFn = (k: string, opts?: { defaultValue?: string }) => string;

/**
 * Canonical, translated host-facing service label for a trip's rental type.
 *
 * Returns the same wording as the host filter chips (Self-Drive / Chauffeur /
 * Event) via the `common.rental_types.*` namespace, so the raw lowercase DB
 * enum ("driving" / "chauffeured" / "event") never leaks into the UI and all
 * host surfaces (booking list + booking detail) agree on one label.
 *
 * `tripType` is an optional fallback for legacy rows where `rental_type` is
 * absent but the numeric/string `trip_type` still marks a chauffeured booking.
 */
export function getServiceLabel(
  rentalType: string | null | undefined,
  t: TranslateFn,
  tripType?: string | number | null,
): string {
  const r = String(rentalType || "").toLowerCase();
  if (
    r === "chauffeured" ||
    r === "chauffeur" ||
    String(tripType) === "1" ||
    tripType === "chauffeured"
  ) {
    return t("common.rental_types.chauffeur");
  }
  if (r === "event") return t("common.rental_types.event", { defaultValue: "Event" });
  return t("common.rental_types.self_drive");
}

/**
 * Proper-cased event-type label for the host booking card pill.
 *
 * `trips.event_type` stores the canonical taxonomy name (e.g. "Wedding",
 * "Company Event"), but we defensively capitalize the first character so any
 * legacy lowercase value never renders raw. Returns `null` for empty input so
 * callers can skip the pill entirely.
 */
export function getEventTypeLabel(eventType: string | null | undefined): string | null {
  const s = String(eventType || "").trim();
  if (!s) return null;
  return s.charAt(0).toUpperCase() + s.slice(1);
}
